import { Component, Input, OnInit } from '@angular/core';
import { IOfficesFilter, IPrimengSelectButtonOptions } from '../../../shared/interfaces';
import { environment } from '../../../../environments/environment';
import { Holding, Office } from '../../../models';
import { BehaviorSubject, Subscription } from 'rxjs';
import { LazyLoadEvent } from 'primeng/api';
import { ApiService, ToastService } from '../../../shared/services';
import { Router } from '@angular/router';

@Component({
  selector: 'app-holding-list',
  templateUrl: './holding-list.component.html',
  styleUrls: ['./holding-list.component.scss'],
})
export class HoldingListComponent implements OnInit {
  @Input() filters: IOfficesFilter | null = {};
  public first = 0;
  public totalRecords = 0;
  public page = 1;
  public pageSize = 1000;
  public availablePageSizes = environment.availablePageSizes;
  public holdings: Holding[] = [];
  public filteredHoldings: Holding[] = [];
  public isLoading = true;

  enabledOptions: IPrimengSelectButtonOptions<'enabled' | 'all'>[] = [
    { label: 'Enabled', value: 'enabled' },
    { label: 'All', value: 'all' },
  ];

  bigPlayerOptions: IPrimengSelectButtonOptions<'big-player' | 'all'>[] = [
    { label: 'Big Players', value: 'big-player' },
    { label: 'All', value: 'all' },
  ];

  selectedEnabledOptionValue = 'enabled';
  selectedBigPlayerOptionValue = 'big-player';

  selectedSearchText = '';
  searchText$: BehaviorSubject<string> = new BehaviorSubject<string>(this.selectedSearchText);

  constructor(
    private readonly apiService: ApiService,
    private readonly toastService: ToastService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.searchText$.subscribe(() => {
      this.selectedValueChanged();
      this.filteredHoldings = this.filteredHoldings.filter((c) =>
        c.name?.toLowerCase().includes(this.selectedSearchText.toLowerCase()),
      );
    });

    this.apiService.holdings.getAll().subscribe({
      next: (data) => {
        this.totalRecords = data.count;
        data.items.forEach((holding) => {
          this.holdings.push(holding);
        });
        this.selectedValueChanged();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.toastService.send({
          severity: 'error',
          summary: $localize`Error`,
          detail: $localize`We could not get holdings from the API`,
        });
      },
    });
  }

  selectedValueChanged() {
    this.filteredHoldings = this.holdings.filter((c) => {
      const isEnabled = this.selectedEnabledOptionValue === 'enabled';
      const isBigPlayer = this.selectedBigPlayerOptionValue === 'big-player';

      if (isEnabled && isBigPlayer) {
        return c.isEnabled && c.isBigPlayer;
      } else if (isEnabled && !isBigPlayer) {
        return c.isEnabled;
      } else if (!isEnabled && isBigPlayer) {
        return c.isBigPlayer;
      } else {
        return c;
      }
    });
  }

  goToHoldingDetailsPage(data: Office): void {
    this.router.navigate(['networks-holdings', 'holding', data.id]);
  }

  load(filters: IOfficesFilter): void {
    this.filters = filters;
  }

  searchTextChanged() {
    this.searchText$.next(this.selectedSearchText);
  }

  public addHolding(holding: Holding) {
    this.holdings.push(holding);
    this.holdings.sort((filterA, filterB) => {
      if (filterA.name > filterB.name) {
        return 1;
      }
      if (filterA.name < filterB.name) {
        return -1;
      }
      return 0;
    });
    this.selectedValueChanged();
  }

  public updateHolding(holding: Holding) {
    const index = this.holdings.findIndex((f) => f.id === holding.id);
    if (index >= 0) {
      this.holdings[index] = holding;
      this.selectedValueChanged();
    }
  }

  public removeHolding(holding: Holding) {
    const index = this.holdings.findIndex((f) => f.id === holding.id);
    if (index >= 0) {
      this.holdings.splice(index, 1);
      this.selectedValueChanged();
    }
  }
}
